import type { ColumnsConfig, RiftConfig } from "../rift/types";

/**
 * Returns a copy of `columnsConfig` containing only keys present in
 * `validColumns`. Entries referencing columns that no longer exist in
 * the published schema are dropped.
 */
export const pruneColumnsConfig = (
  columnsConfig: ColumnsConfig | null | undefined,
  validColumns: Set<string>,
): ColumnsConfig => {
  if (!columnsConfig) return {};

  const pruned: ColumnsConfig = {};
  for (const [key, value] of Object.entries(columnsConfig)) {
    if (validColumns.has(key)) {
      pruned[key] = value;
    }
  }
  return pruned;
};

/**
 * Returns a copy of `riftConfig` with stale column references removed
 * from `sorts`, `groups`, `aggregates`, `footers`, and `hiddenColumns`.
 * Fields that don't reference columns (`limit`, `filters`) are preserved as-is.
 */
export const pruneRiftConfig = (
  riftConfig: RiftConfig | null | undefined,
  validColumns: Set<string>,
): RiftConfig | null => {
  if (!riftConfig) return null;

  return {
    ...riftConfig,
    sorts: riftConfig.sorts?.filter((s) => validColumns.has(s.column)) ?? [],
    groups: filterRecord(riftConfig.groups, validColumns),
    aggregates: filterRecord(riftConfig.aggregates, validColumns),
    footers: filterRecord(riftConfig.footers, validColumns),
    hiddenColumns:
      riftConfig.hiddenColumns?.filter((c) => validColumns.has(c)) ?? [],
  };
};

/** Keeps only record entries whose key is in the valid set. */
const filterRecord = <T>(
  record: Record<string, T> | undefined,
  validKeys: Set<string>,
): Record<string, T> => {
  if (!record) return {};

  const result: Record<string, T> = {};
  for (const [key, value] of Object.entries(record)) {
    if (validKeys.has(key)) {
      result[key] = value;
    }
  }
  return result;
};
